import { createStyles, Group, Stack } from '@mantine/core';
import { EntityType } from '@repo/common/enums/entityType';
import { Text, Title } from '@repo/foundations';
import { forwardRef, useCallback } from 'react';
import type { SearchResult } from '../../hooks/useSearch';
import { EntityIcon } from '../EntityIcon/EntityIcon';

interface ISelectItemComponentProps {
	label: string;
	value: string;
	entity: SearchResult;
}

const useStyles = createStyles((theme) => ({
	wrapper: {
		cursor: 'pointer',
		borderRadius: theme.radius.md,
		gap: theme.spacing.xs,
		flexWrap: 'nowrap',
		'&:hover': {
			backgroundColor: theme.other.getColor('surface/primary/hover'),
		},
		'&:active': {
			backgroundColor: theme.other.getColor('surface/primary/active'),
		},
	},
}));

const SelectItemComponent = forwardRef<
	HTMLDivElement,
	ISelectItemComponentProps
>(({ entity, ...others }: ISelectItemComponentProps, ref) => {
	const { classes } = useStyles();

	const generateSubtitle = useCallback(
		(entityType: EntityType, searchMetadata?: Record<string, string>) => {
			if (!searchMetadata) {
				return '';
			}

			const metadataMap = {
				[EntityType.table]: ['database', 'schema'],
				[EntityType.column]: ['database', 'schema', 'table'],
				[EntityType.dashboard]: ['product', 'group'],
				[EntityType.chart]: ['group'],
				[EntityType.job]: ['group'],
			} as const;

			const fields = metadataMap[entityType as keyof typeof metadataMap];
			if (!fields) {
				return '';
			}

			if (fields.length === 1) {
				return searchMetadata[fields[0]] || '';
			}

			return fields
				.map((field) => searchMetadata[field])
				.filter(Boolean)
				.join(' / ');
		},
		[]
	);

	return (
		<div ref={ref} {...others}>
			<Group className={classes.wrapper}>
				<EntityIcon
					entityType={entity.entity_type}
					integrationType={entity.integration_type ?? undefined}
					size={20}
				/>
				<Stack spacing={0}>
					<Title size="sm" truncate>
						{entity.title}
					</Title>
					<Text size="xs" color="text/secondary/default" truncate>
						{generateSubtitle(entity.entity_type, entity.search_metadata)}
					</Text>
				</Stack>
			</Group>
		</div>
	);
});

SelectItemComponent.displayName = 'SelectItemComponent';

export default SelectItemComponent;
